import { type ColumnDef } from "@tanstack/react-table"
import { type TFunction } from "i18next"
import { Circle, CircleCheck } from "lucide-react"
import { toast } from "react-toastify"
import ClockIcon from "@/assets/icons/misc/clock-icon.tsx"
import EmptyCalendarIcon from "@/assets/icons/misc/empty-calendar-icon.tsx"
import { type Animal } from "@/features/farm"
import { getTaskState, TASK_STATES } from "@/features/tasks"
import { type TaskCategory } from "@/features/tasks/api/task-categories-api.ts"
import { type useToggleTaskStatusMutation } from "@/features/tasks/api/tasks-api.ts"
import {
  getCategoryForTask,
  getTaskDateHour,
} from "@/features/tasks/components/table/task-table-utils.ts"
import TaskCardPillAnimalInfo from "@/features/tasks/components/task-card-pills/task-card-pill-animal-info.tsx"
import TaskCardPillCategoryInfo from "@/features/tasks/components/task-card-pills/task-card-pill-category-info.tsx"
import { type Task } from "@/features/tasks/types/task.ts"
import { cn } from "@/utils/styles.ts"

export interface TaskTableDeps {
  t: TFunction
  animals: Animal[]
  categories: TaskCategory[]
  toggleTaskStatus: ReturnType<typeof useToggleTaskStatusMutation>[0]
}

export const getTaskTableColumns = ({
  t,
  animals,
  categories,
  toggleTaskStatus,
}: TaskTableDeps): ColumnDef<Task>[] => [
  {
    accessorKey: "is_done",
    header: t("status"),
    cell: ({ row }) => {
      const task = row.original
      return (
        <div
          className="group w-fit cursor-pointer rounded-full p-1 transition-colors [@media(hover:hover)]:hover:bg-green-50"
          onClick={(e) => {
            e.stopPropagation()
            toast.promise(toggleTaskStatus(task.id).unwrap(), {
              pending: t("updatingTask"),
              success: t("taskUpdated"),
              error: t("errorUpdatingTask"),
            })
          }}
        >
          {task.is_done ? (
            <CircleCheck className="h-[25px] w-[25px] text-green-600" />
          ) : (
            <Circle className="h-[25px] w-[25px] text-neutral-300 transition-colors [@media(hover:hover)]:group-hover:text-green-600" />
          )}
        </div>
      )
    },
    sortingFn: (rowA, rowB) =>
      Number(rowA.original.is_done) - Number(rowB.original.is_done),
  },
  {
    accessorKey: "title",
    header: t("title"),
    cell: ({ row }) => {
      const task = row.original
      return (
        <div className="flex items-center gap-2">
          <span className="font-medium">
            {task.title.charAt(0).toUpperCase() + task.title.slice(1)}
          </span>
          {Object.hasOwn(task, "hasChanges") && (
            <i className="text-[10px] text-amber-600">
              {t("unsavedChangesIndicator")}
            </i>
          )}
        </div>
      )
    },
  },
  {
    accessorKey: "category",
    header: t("category"),
    enableSorting: false,
    cell: ({ row }) => {
      const task = row.original
      const taskCategory = getCategoryForTask(task, categories)
      const taskAnimals = animals.filter((a) => task.animals?.includes(a.id))

      const totalEntities =
        (task.animals?.length ?? 0) +
        (task.crop_plantings?.length ?? 0) +
        (task.queens?.length ?? 0) +
        (task.hives?.length ?? 0) +
        (task.parcels?.length ?? 0) +
        (task.apiaries?.length ?? 0) +
        (task.birds_groups?.length ?? 0)

      const animalEntitiesCondition =
        taskAnimals.length > 0 && task.animals.length === totalEntities
      const moreEntitiesCondition =
        task.animals &&
        task.animals.length !== totalEntities &&
        totalEntities > 0

      return (
        <div className="flex items-center gap-2">
          {taskCategory && (
            <TaskCardPillCategoryInfo taskCategory={taskCategory} />
          )}
          {animalEntitiesCondition && (
            <TaskCardPillAnimalInfo animals={taskAnimals} />
          )}
          {moreEntitiesCondition && (
            <div className="grid h-[30px] w-fit items-center rounded-[15px] border border-neutral-200 bg-white px-2.5 text-sm">
              {totalEntities} {t("entities")}
            </div>
          )}
        </div>
      )
    },
  },
  {
    accessorKey: "due_date",
    header: t("Date"),
    cell: ({ row }) => {
      const task = row.original
      const { formattedDate, formattedHour } = getTaskDateHour(task)
      const taskState = getTaskState(task, false)
      const hourStyle =
        taskState === TASK_STATES.PAST ? "text-red-500" : "text-neutral-700"

      return formattedDate && formattedHour ? (
        <div className="flex items-center gap-2 text-sm">
          <EmptyCalendarIcon
            size={12}
            color={
              taskState === TASK_STATES.PAST
                ? "var(--color-red-500)"
                : undefined
            }
          />
          <span className={cn("flex items-center gap-2", hourStyle)}>
            {formattedDate}
            <span className="flex items-center gap-1">
              <ClockIcon className={cn("h-3 w-3", hourStyle)} />
              {formattedHour}
            </span>
          </span>
        </div>
      ) : (
        <span className="text-gray-400">—</span>
      )
    },
    sortingFn: (rowA, rowB) => {
      const dateA = rowA.original.due_date
        ? new Date(rowA.original.due_date).getTime()
        : Infinity
      const dateB = rowB.original.due_date
        ? new Date(rowB.original.due_date).getTime()
        : Infinity
      return dateA - dateB
    },
  },
]
